import {
  AllocationPaymentItem,
  AllocationPaymentTargetAccount,
  BaseCurrency,
  GetAllocationPaymentDetailsResponse,
  isDefined,
  Loadable,
  LoadableState,
  NbSelectItemOption,
  useDictionary,
} from '@eih-netbroker/common';
import axios, { isAxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useLocale } from '../../../context/NbLocalizationContext';
import { useAccounts } from '../../../shared/services/useAccounts';
import { CashActionStep, getTargetAccountAborterAtom } from '../states/cashActionAtoms';
import { usePayment } from './usePayment';

export const useAllocation = () => {
  const { formatDictionaryType } = useDictionary();
  const { getMainClientId } = useAccounts();
  const {
    setCashActionStep,
    cashActionTemp,
    cashActionStep,
    setCashActionTemp,
    getAllocationPaymentDetails,
    getAllocationPaymentTargetAccounts,
    submitAllocationPayment,
    confirmAllocationPayment,
    setTempId,
    tempId,
    setReceivedMessages,
    handleTwoFactorError,
    clearPaymentAtomState,
    smsCodeError,
    canRestartTwoFactor,
    handleTwoFactorRestart,
  } = usePayment();
  const [allocationDetails, setAllocationDetails] = useState<LoadableState<GetAllocationPaymentDetailsResponse>>(
    Loadable.uninitialized,
  );
  const [targetAccounts, setTargetAccounts] = useState<LoadableState<Array<AllocationPaymentTargetAccount>>>(
    Loadable.uninitialized,
  );
  const [selectedAllocationCurrency, setSelectedAllocationCurrency] = useState<string>('');
  const [selectedSourceAccount, setSelectedSourceAccount] = useState<AllocationPaymentItem | undefined>(undefined);
  const [selectedTargetAccount, setSelectedTargetAccount] = useState<AllocationPaymentTargetAccount | undefined>(
    undefined,
  );
  const { translation } = useLocale();
  const [getTargetAccountAborter, setGetTargetAccountAborter] = useAtom(getTargetAccountAborterAtom);

  const sourceAccountOptions = useMemo<LoadableState<Array<NbSelectItemOption<AllocationPaymentItem>>>>(() => {
    if (allocationDetails.isLoading) {
      return Loadable.loading;
    }

    if (allocationDetails.hasError) {
      return Loadable.failed;
    }

    if (allocationDetails.isLoaded) {
      return Loadable.loaded(
        allocationDetails.allocationPaymentItems
          .filter(acc => acc.currencyCode === selectedAllocationCurrency)
          .map(acc => ({
            id: acc,
            name: `${formatDictionaryType(acc.accountCategory, 'accountCategoryTypes')} - ${acc.clientId}`,
          })),
      );
    }

    return Loadable.uninitialized;
  }, [formatDictionaryType, selectedAllocationCurrency, allocationDetails]);

  const targetAccountOptions = useMemo<LoadableState<Array<NbSelectItemOption<AllocationPaymentTargetAccount>>>>(() => {
    if (targetAccounts.isLoading) {
      return Loadable.loading;
    }

    if (targetAccounts.hasError) {
      return Loadable.failed;
    }

    if (targetAccounts.isLoaded) {
      return Loadable.loaded(
        targetAccounts
          .filter(acc => acc.currencyCode === selectedAllocationCurrency)
          .map(acc => ({
            id: acc,
            name: acc.accountNumber,
          })),
      );
    }

    return Loadable.uninitialized;
  }, [targetAccounts, selectedAllocationCurrency]);

  const currencyOptions = useMemo<Array<NbSelectItemOption<string>>>(
    () =>
      allocationDetails.isLoaded
        ? Array.from(new Set(allocationDetails.allocationPaymentItems.map(c => c.currencyCode))).map(c => ({
            id: c,
            name: translation.portfolio.currencies[c as keyof typeof translation.portfolio.currencies] ?? c,
          }))
        : [],
    [translation, allocationDetails],
  );

  const handleAllocationSubmit = useCallback(
    async (onConfirm: () => void) => {
      if (
        !isDefined(cashActionTemp) ||
        !isDefined(cashActionTemp.sourceAccountCategory) ||
        !isDefined(cashActionTemp.targetAccountNumber)
      ) {
        return;
      }

      try {
        const resp = await submitAllocationPayment({
          submitAllocationPaymentRequest: {
            amount: parseFloat(cashActionTemp.amount),
            targetAccountNumber: cashActionTemp.targetAccountNumber,
            targetClientId: cashActionTemp.targetClientId,
            currencyCode: cashActionTemp.currencyCode,
            sourceAccountCategory: cashActionTemp.sourceAccountCategory,
            sourceClientId: cashActionTemp.sourceClientId,
          },
        });

        if (isDefined(resp.data.messages) && resp.data.messages.length > 0) {
          if (resp.data.responseType === 'submitted') {
            setTempId(resp.data.tempId);
          }
          setReceivedMessages(resp.data.messages);
          setCashActionStep(CashActionStep.ReceivedMessages);
          return;
        }

        if (resp.data.responseType === 'submitted') {
          setTempId(resp.data.tempId);
          setCashActionStep(CashActionStep.Confirm);
        } else {
          onConfirm();
        }
      } catch (error) {
        setReceivedMessages([{ msgType: 'error', msg: translation.cashAction.errorsCommon.cannotSubmit }]);
        setCashActionStep(CashActionStep.ReceivedMessages);
      }
    },
    [cashActionTemp, submitAllocationPayment, setTempId, setCashActionStep, setReceivedMessages, translation],
  );

  const handleAllocationConfirm = useCallback(
    async (smsCode: string, onSuccess: () => void) => {
      if (
        !isDefined(cashActionTemp) ||
        !isDefined(tempId) ||
        !isDefined(cashActionTemp.targetAccountNumber) ||
        !isDefined(cashActionTemp.sourceAccountCategory)
      ) {
        return;
      }

      try {
        const resp = await confirmAllocationPayment({
          tempId,
          confirmAllocationPaymentRequest: {
            smsCode,
            amount: parseFloat(cashActionTemp.amount),
            targetAccountNumber: cashActionTemp.targetAccountNumber,
            targetClientId: cashActionTemp.targetClientId,
            currencyCode: cashActionTemp.currencyCode,
            sourceClientId: cashActionTemp.sourceClientId,
            sourceAccountCategory: cashActionTemp.sourceAccountCategory,
          },
        });

        if (isDefined(resp.data.messages) && resp.data.messages.length > 0) {
          setTempId(undefined);
          setReceivedMessages(resp.data.messages);
          setCashActionStep(CashActionStep.ReceivedMessages);
          return;
        }

        if (resp.data.responseType === 'confirmed') {
          onSuccess();
        }
      } catch (error) {
        handleTwoFactorError(error);
      }
    },
    [
      cashActionTemp,
      confirmAllocationPayment,
      handleTwoFactorError,
      setCashActionStep,
      setReceivedMessages,
      setTempId,
      tempId,
    ],
  );

  const getTargetAccounts = useCallback(
    async (sourceAccount: AllocationPaymentItem, abortController: AbortController) => {
      setTargetAccounts(Loadable.loading);
      setSelectedTargetAccount(undefined);

      try {
        const targetAccounts = (
          await getAllocationPaymentTargetAccounts(
            {
              clientId: sourceAccount.clientId,
              currencyCode: sourceAccount.currencyCode,
              accountCategory: sourceAccount.accountCategory,
            },
            abortController,
          )
        ).data.allocationPaymentTargetAccounts;

        setTargetAccounts(Loadable.loaded(targetAccounts));
        setSelectedTargetAccount(targetAccounts[0]);
      } catch (error) {
        if (isAxiosError(error) && axios.isCancel(error)) {
          return;
        }

        setTargetAccounts(Loadable.failed);
        setSelectedTargetAccount(undefined);
      }
    },
    [getAllocationPaymentTargetAccounts, setSelectedTargetAccount, setTargetAccounts],
  );

  const refreshAllocationDetails = useCallback(async () => {
    getTargetAccountAborter?.abort();

    setAllocationDetails(Loadable.loading);
    try {
      const resp = (await getAllocationPaymentDetails(getMainClientId())).data;
      setAllocationDetails(Loadable.loaded(resp));

      if (resp.allocationPaymentItems.length > 0) {
        setSelectedAllocationCurrency(resp.allocationPaymentItems[0].currencyCode);
        setSelectedSourceAccount(resp.allocationPaymentItems[0]);
        const abortController = new AbortController();
        setGetTargetAccountAborter(abortController);
        getTargetAccounts(resp.allocationPaymentItems[0], abortController);
      } else {
        setSelectedAllocationCurrency('');
        setSelectedSourceAccount(undefined);
        setSelectedTargetAccount(undefined);
        setTargetAccounts(Loadable.loaded([]));
      }
    } catch {
      setAllocationDetails(Loadable.failed);
    }
  }, [
    getTargetAccounts,
    getMainClientId,
    setAllocationDetails,
    getAllocationPaymentDetails,
    setGetTargetAccountAborter,
    getTargetAccountAborter,
  ]);

  const handleModalClose = useCallback(
    (onClose: () => void) => {
      clearPaymentAtomState();
      setSelectedAllocationCurrency(BaseCurrency);
      onClose();
    },
    [clearPaymentAtomState, setSelectedAllocationCurrency],
  );

  const handleSourceAccountChange = useCallback(
    (value: string | AllocationPaymentItem) => {
      getTargetAccountAborter?.abort();
      if (typeof value === 'string' || !allocationDetails.isLoaded) {
        setTargetAccounts(Loadable.loaded([]));
        setSelectedTargetAccount(undefined);
        setSelectedSourceAccount(undefined);
        return;
      }

      setSelectedSourceAccount(value);
      const abortController = new AbortController();
      setGetTargetAccountAborter(abortController);
      getTargetAccounts(value, abortController);
    },
    [getTargetAccounts, setGetTargetAccountAborter, allocationDetails, getTargetAccountAborter],
  );

  const handleTargetAccountChange = useCallback(
    (value: string | AllocationPaymentTargetAccount) => {
      if (typeof value === 'string') {
        setSelectedTargetAccount(undefined);
        return;
      }

      setSelectedTargetAccount(value);
    },
    [setSelectedTargetAccount],
  );

  return {
    handleAllocationConfirm,
    handleAllocationSubmit,
    handleModalClose,
    refreshAllocationDetails,
    handleSourceAccountChange,
    handleTargetAccountChange,
    setCashActionTemp,
    setCashActionStep,
    setSelectedAllocationCurrency,
    selectedSourceAccount,
    currencyOptions,
    selectedTargetAccount,
    targetAccounts,
    sourceAccountOptions,
    targetAccountOptions,
    cashActionTemp,
    cashActionStep,
    selectedAllocationCurrency,
    smsCodeError,
    allocationDetails,
    canRestartTwoFactor,
    handleTwoFactorRestart,
  };
};
